/**
 * useScheduler Hook
 *
 * Core hook for managing the entire scheduler system, including:
 * - Fetching and managing calendar events, notifications, and schedules
 * - Creating, editing, and deleting notifications (both single and recurring)
 * - Optimistic UI updates with rollback on error
 * - Multi-language support with background data prefetching
 * - Temporary event tracking for edit-in-progress scenarios
 * - Exercise type preferences and validation
 * - Video library and watch history management
 *
 * Uses TanStack Query for server state management and Zustand for auth context.
 * Implements optimistic updates with Map-based tracking for temp events.
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import schedulerApi from "../services/api/SchedulerApiService";
import settingsApiService from "../services/api/SettingsApiService";
import { useAuthStore } from "../stores/zustand";
import { useHomeDataQuery } from "../stores/tanstack/queries/useHomeDataQuery";
import { usePreferencesQuery } from "../stores/tanstack/queries/usePreferencesQuery";
import { useVideosQuery } from "../stores/tanstack/queries/useVideosQuery";
import { getRandomVideoForPhysio } from "../util/videoHelpers";
import axios from "axios";
import { preloadThumbnails } from "../util/preloadThumbnails";

export const useScheduler = () => {
  // Query client for cache management and mutation triggering
  // Auth context: language, email for query key generation
  // Query client for cache management and mutation triggering
  // Auth context: language, email for query key generation
  const queryClient = useQueryClient();
  const { setUser } = useAuthStore();
  const currentLanguage = useAuthStore((state) => state.preferredLanguage);
  const email = useAuthStore((state) => state.user.email);

  // Fetch home data (notifications/schedules), user preferences, and available videos
  // These hooks drive the main calendar display and form options

  // Fetch home data (notifications/schedules), user preferences, and available videos
  // These hooks drive the main calendar display and form options
  const {
    data: preferencesData,
    isLoading: preferencesLoading,
    error: preferencesError,
  } = usePreferencesQuery();

  const {
    data: videosData,
    isLoading: videosLoading,
    error: videosError,
  } = useVideosQuery();

  const {
    data: homeData,
    isLoading: homeLoading,
    isFetching: homeFetching,
    error: homeError,
  } = useHomeDataQuery(videosData);

  // Extract calendar events, notifications, and schedules from home data
  // Filter out "Random" preference type from exercise types list
  const calendarEvents = homeData?.calendarEvents || [];
  const schedules = homeData?.schedules || [];
  const exerciseTypes =
    preferencesData?.rawPreferences?.filter((pref) => pref.name !== "Random") ||
    [];

  // Extract userId from server response and update global auth context
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const rawHomeData = homeData?.rawHomeData;
    if (rawHomeData) {
      setUser({
        id: rawHomeData.id,
        name: rawHomeData.fullName || rawHomeData.name,
        email: rawHomeData.email,
        language: rawHomeData.language,
      });
      setUserId(rawHomeData.id);
    }
  }, [homeData, setUser]);

  // Calculate timezone offset for API requests (format: "+02:00" or "-05:00")
  const timezoneOffset = useMemo(() => {
    const offset = new Date().getTimezoneOffset() / -60;
    return offset >= 0
      ? `+${String(Math.abs(offset)).padStart(2, "0")}`
      : `-${String(Math.abs(offset)).padStart(2, "0")}`;
  }, []);

  // Generate unique temporary IDs for optimistic updates before server confirmation
  const generateTempId = () => `temp-${crypto.randomUUID()}`;

  // Build TanStack Query cache key with language and timezone context
  const getQueryKey = useCallback(
    (lang) => ["homeData", email, timezoneOffset, lang],
    [email, timezoneOffset],
  );

  // Refetch current language data and wait for completion
  //  blocks on refetch to ensure real data is in cache before removing optimistic state
  const refetchCurrentLanguageAndWait = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: getQueryKey(currentLanguage),
      exact: true,
    });
    await queryClient.refetchQueries({
      queryKey: getQueryKey(currentLanguage),
      exact: true,
    });
  }, [queryClient, currentLanguage, getQueryKey]);

  // Invalidate other language cache in the background
  // Deferred invalidation allows user's primary language to load first
  const invalidateOtherLanguage = useCallback(() => {
    const otherLanguage = currentLanguage === "en" ? "de" : "en";
    queryClient.invalidateQueries({
      queryKey: getQueryKey(otherLanguage),
      exact: true,
    });
  }, [queryClient, currentLanguage, getQueryKey]);

  // Refetch sequence: wait for current language, then background invalidate other
  // Optimized for user experience: immediate response in user's language
  const silentRefetchSequence = useCallback(async () => {
    await refetchCurrentLanguageAndWait();
    setTimeout(invalidateOtherLanguage, 1500);
  }, [refetchCurrentLanguageAndWait, invalidateOtherLanguage]);

  // CREATE NOTIFICATION MUTATION
  const createMutation = useMutation({
    mutationFn: async (notificationData) => {
      const result = await schedulerApi.createNotification(notificationData);
      return result;
    },

    onMutate: async (notificationData) => {
      // Cancel outgoing queries to prevent race conditions
      await queryClient.cancelQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Snapshot current cache for rollback
      const previousHomeData = queryClient.getQueryData(
        getQueryKey(currentLanguage),
      );

      // Generate temp IDs
      const tempNotifId = generateTempId();
      const tempScheduleId = generateTempId();

      // Get exercise type name for event title
      const firstPreferenceId = notificationData.preferenceIds?.[0];
      const firstPreference = exerciseTypes.find(
        (type) => type.id === firstPreferenceId,
      );

      // Determine display title based on user type
      let tempTitle;
      if (firstPreferenceId === -1) {
        // Physio user - placeholder based on language
        tempTitle =
          currentLanguage === "de" ? "Terminplanung..." : "Scheduling...";
      } else {
        // Regular user - use preference name
        tempTitle =
          firstPreference?.name || notificationData.name || "New Event";
      }

      // Create temporary schedule object
      const tempSchedule = {
        id: tempScheduleId,
        name: tempTitle,
        description: notificationData.description,
        creationDate: new Date().toISOString(),
        notificationTime: notificationData.notificationTime,
        repeatPeriod: notificationData.repeatPeriod,
        repeatInterval: notificationData.repeatInterval,
        preferenceId: firstPreferenceId,
        notificationId: tempNotifId,
        type: "USER",
        video: null,
      };

      // Create temporary notification object
      const tempNotification = {
        id: tempNotifId,
        name: tempTitle,
        description: notificationData.description,
        creationDate: new Date().toISOString(),
        notificationTime: notificationData.notificationTime,
        repeatPeriod: notificationData.repeatPeriod,
        repeatInterval: notificationData.repeatInterval,
        preferenceId: firstPreferenceId,
        type: "USER",
        schedules: [tempSchedule],
        video: null,
      };

      // Optimistically update cache (notifications and schedules only)
      // Calendar events will be re-derived from notifications
      queryClient.setQueryData(getQueryKey(currentLanguage), (old) => {
        if (!old) return old;

        const newNotifications = [...old.notifications, tempNotification];
        const newSchedules = [...old.schedules, tempSchedule];

        // Re-derive calendar events with updated notifications
        const newCalendarEvents = deriveCalendarEventsInScheduler(
          newNotifications,
          videosData || { allVideos: [], physioVideos: [] },
        );

        return {
          ...old,
          notifications: newNotifications,
          schedules: newSchedules,
          calendarEvents: newCalendarEvents,
        };
      });

      // Return context for success/error handlers
      return {
        previousHomeData,
        tempNotifId,
        tempScheduleId,
      };
    },

    onSuccess: async () => {
      // Force refetch to get real data from server
      await queryClient.refetchQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Background invalidate other language
      setTimeout(() => {
        const otherLanguage = currentLanguage === "en" ? "de" : "en";
        queryClient.invalidateQueries({
          queryKey: getQueryKey(otherLanguage),
          exact: true,
        });
      }, 1500);
    },

    onError: (error, variables, context) => {
      // Rollback to previous state
      if (context?.previousHomeData) {
        queryClient.setQueryData(
          getQueryKey(currentLanguage),
          context.previousHomeData,
        );
      }
    },
  });

  // EDIT NOTIFICATION MUTATION
  const editNotificationMutation = useMutation({
    mutationFn: async ({ notificationId, notificationData }) => {
      const result = await schedulerApi.editNotification(
        notificationId,
        notificationData,
      );
      return result;
    },

    onMutate: async ({ notificationId, notificationData }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Snapshot current cache
      const previousHomeData = queryClient.getQueryData(
        getQueryKey(currentLanguage),
      );

      // Find old notification
      const oldNotif = previousHomeData?.notifications.find(
        (n) => n.id === notificationId,
      );
      if (!oldNotif) {
        throw new Error("Notification not found");
      }

      // Generate temp IDs
      const tempNotifId = generateTempId();
      const tempScheduleId = generateTempId();

      // Create optimistic notification with updated data
      const optimisticNotif = {
        ...oldNotif,
        id: tempNotifId,
        name: notificationData.name,
        description: notificationData.description,
        notificationTime: notificationData.notificationTime,
        repeatPeriod: notificationData.repeatPeriod,
        repeatInterval: notificationData.repeatInterval,
        preferenceId: notificationData.preferenceIds[0],
        schedules: oldNotif.schedules.map((s, i) => ({
          ...s,
          id: i === 0 ? tempScheduleId : generateTempId(),
          name: notificationData.name,
          description: notificationData.description,
          notificationTime: notificationData.notificationTime,
          repeatPeriod: notificationData.repeatPeriod,
          repeatInterval: notificationData.repeatInterval,
          preferenceId: notificationData.preferenceIds[0],
          notificationId: tempNotifId,
        })),
      };

      // Optimistically update cache
      queryClient.setQueryData(getQueryKey(currentLanguage), (old) => {
        if (!old) return old;

        // Replace old notification with optimistic one
        const newNotifications = old.notifications.map((n) =>
          n.id === notificationId ? optimisticNotif : n,
        );

        // Remove old schedules and add optimistic ones
        const newSchedules = [
          ...old.schedules.filter((s) => s.notificationId !== notificationId),
          ...optimisticNotif.schedules,
        ];

        // Re-derive calendar events with updated notifications
        const newCalendarEvents = deriveCalendarEventsInScheduler(
          newNotifications,
          videosData || { allVideos: [], physioVideos: [] },
        );

        return {
          ...old,
          notifications: newNotifications,
          schedules: newSchedules,
          calendarEvents: newCalendarEvents,
        };
      });

      // Return context
      return {
        previousHomeData,
        oldNotifId: notificationId,
        tempNotifId,
        tempScheduleId,
      };
    },

    onSuccess: async () => {
      // Force refetch to get real data from server
      await queryClient.refetchQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Background invalidate other language
      setTimeout(() => {
        const otherLanguage = currentLanguage === "en" ? "de" : "en";
        queryClient.invalidateQueries({
          queryKey: getQueryKey(otherLanguage),
          exact: true,
        });
      }, 1500);
    },

    onError: (error, variables, context) => {
      // Rollback to previous state
      if (context?.previousHomeData) {
        queryClient.setQueryData(
          getQueryKey(currentLanguage),
          context.previousHomeData,
        );
      }
    },
  });

  // EDIT SCHEDULE MUTATION
  const editScheduleMutation = useMutation({
    mutationFn: async ({ scheduleId, notificationData }) => {
      const result = await schedulerApi.editSchedule(
        scheduleId,
        notificationData,
      );
      return result;
    },

    onMutate: async ({ scheduleId, notificationData }) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Snapshot current cache
      const previousHomeData = queryClient.getQueryData(
        getQueryKey(currentLanguage),
      );

      // Find old schedule and its parent notification
      const oldSchedule = previousHomeData?.schedules.find(
        (s) => s.id === scheduleId,
      );
      if (!oldSchedule) {
        throw new Error("Schedule not found");
      }

      const oldNotif = previousHomeData?.notifications.find(
        (n) => n.id === oldSchedule.notificationId,
      );
      if (!oldNotif) {
        throw new Error("Notification not found");
      }

      // Generate temp IDs
      const tempNotifId = generateTempId();
      const tempScheduleId = generateTempId();

      // Create optimistic notification with updated schedule
      const optimisticNotif = {
        ...oldNotif,
        id: tempNotifId,
        name: notificationData.name,
        description: notificationData.description,
        notificationTime: notificationData.notificationTime,
        repeatPeriod: notificationData.repeatPeriod,
        repeatInterval: notificationData.repeatInterval,
        preferenceId: notificationData.preferenceIds[0],
        schedules: oldNotif.schedules.map((s) => {
          if (s.id === scheduleId) {
            // Update the schedule being edited
            return {
              ...s,
              id: tempScheduleId,
              name: notificationData.name,
              description: notificationData.description,
              notificationTime: notificationData.notificationTime,
              repeatPeriod: notificationData.repeatPeriod,
              repeatInterval: notificationData.repeatInterval,
              preferenceId:
                oldSchedule.type === "PHYSIO"
                  ? s.preferenceId
                  : notificationData.preferenceIds[0],
              notificationId: tempNotifId,
            };
          }
          // Keep other schedules unchanged, just update notificationId reference
          return {
            ...s,
            notificationId: tempNotifId,
          };
        }),
      };

      // Optimistically update cache
      queryClient.setQueryData(getQueryKey(currentLanguage), (old) => {
        if (!old) return old;

        // Replace old notification with optimistic one
        const newNotifications = old.notifications.map((n) =>
          n.id === oldNotif.id ? optimisticNotif : n,
        );

        // Remove old schedules and add optimistic ones
        const newSchedules = [
          ...old.schedules.filter((s) => s.id !== scheduleId),
          ...optimisticNotif.schedules,
        ];

        // Re-derive calendar events with updated notifications
        const newCalendarEvents = deriveCalendarEventsInScheduler(
          newNotifications,
          videosData || { allVideos: [], physioVideos: [] },
        );

        return {
          ...old,
          notifications: newNotifications,
          schedules: newSchedules,
          calendarEvents: newCalendarEvents,
        };
      });

      // Return context
      return {
        previousHomeData,
        oldScheduleId: scheduleId,
        oldNotifId: oldNotif.id,
        tempScheduleId,
      };
    },

    onSuccess: async () => {
      // Force refetch to get real data from server
      await queryClient.refetchQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Background invalidate other language
      setTimeout(() => {
        const otherLanguage = currentLanguage === "en" ? "de" : "en";
        queryClient.invalidateQueries({
          queryKey: getQueryKey(otherLanguage),
          exact: true,
        });
      }, 1500);
    },

    onError: (error, variables, context) => {
      // Rollback to previous state
      if (context?.previousHomeData) {
        queryClient.setQueryData(
          getQueryKey(currentLanguage),
          context.previousHomeData,
        );
      }
    },
  });

  // DELETE NOTIFICATION MUTATION
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId) => {
      return await schedulerApi.deleteNotification(notificationId);
    },

    onMutate: async (notificationId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Snapshot current cache
      const previousHomeData = queryClient.getQueryData(
        getQueryKey(currentLanguage),
      );

      // Find notification to delete
      const oldNotif = previousHomeData?.notifications.find(
        (n) => n.id === notificationId,
      );
      if (!oldNotif) {
        return { previousHomeData };
      }

      const scheduleIds = oldNotif.schedules.map((s) => s.id);

      // Optimistically remove from cache
      queryClient.setQueryData(getQueryKey(currentLanguage), (old) => {
        if (!old) return old;

        // Remove notification and its schedules
        const newNotifications = old.notifications.filter(
          (n) => n.id !== notificationId,
        );
        const newSchedules = old.schedules.filter(
          (s) => !scheduleIds.includes(s.id),
        );

        // Re-derive calendar events with updated notifications
        const newCalendarEvents = deriveCalendarEventsInScheduler(
          newNotifications,
          videosData || { allVideos: [], physioVideos: [] },
        );

        return {
          ...old,
          notifications: newNotifications,
          schedules: newSchedules,
          calendarEvents: newCalendarEvents,
        };
      });

      // Return context
      return { previousHomeData, oldNotifId: notificationId };
    },

    onSuccess: async () => {
      // Force refetch to get real data from server
      await queryClient.refetchQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Background invalidate other language
      setTimeout(() => {
        const otherLanguage = currentLanguage === "en" ? "de" : "en";
        queryClient.invalidateQueries({
          queryKey: getQueryKey(otherLanguage),
          exact: true,
        });
      }, 1500);
    },

    onError: (error, variables, context) => {
      // Rollback to previous state
      if (context?.previousHomeData) {
        queryClient.setQueryData(
          getQueryKey(currentLanguage),
          context.previousHomeData,
        );
      }
    },
  });

  // DELETE SCHEDULE MUTATION
  const deleteScheduleMutation = useMutation({
    mutationFn: async (scheduleId) => {
      return await schedulerApi.deleteSchedule(scheduleId);
    },

    onMutate: async (scheduleId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Snapshot current cache
      const previousHomeData = queryClient.getQueryData(
        getQueryKey(currentLanguage),
      );

      // Optimistically remove from cache
      queryClient.setQueryData(getQueryKey(currentLanguage), (old) => {
        if (!old) return old;

        // Remove schedule from notifications' nested schedules
        const newNotifications = old.notifications.map((notif) => ({
          ...notif,
          schedules: notif.schedules.filter((s) => s.id !== scheduleId),
        }));

        const newSchedules = old.schedules.filter((s) => s.id !== scheduleId);

        // Re-derive calendar events with updated notifications
        const newCalendarEvents = deriveCalendarEventsInScheduler(
          newNotifications,
          videosData || { allVideos: [], physioVideos: [] },
        );

        return {
          ...old,
          notifications: newNotifications,
          schedules: newSchedules,
          calendarEvents: newCalendarEvents,
        };
      });

      // Return context
      return { previousHomeData, oldScheduleId: scheduleId };
    },

    onSuccess: async () => {
      // Force refetch to get real data from server
      await queryClient.refetchQueries({
        queryKey: getQueryKey(currentLanguage),
        exact: true,
      });

      // Background invalidate other language
      setTimeout(() => {
        const otherLanguage = currentLanguage === "en" ? "de" : "en";
        queryClient.invalidateQueries({
          queryKey: getQueryKey(otherLanguage),
          exact: true,
        });
      }, 1500);
    },

    onError: (error, variables, context) => {
      // Rollback to previous state
      if (context?.previousHomeData) {
        queryClient.setQueryData(
          getQueryKey(currentLanguage),
          context.previousHomeData,
        );
      }
    },
  });

  const findNotificationConflict = useCallback(
    async (notificationTime, excludeNotificationId = null) => {
      try {
        const result = await schedulerApi.validateNotificationTime(
          notificationTime,
          excludeNotificationId,
        );

        return result;
      } catch (error) {
        throw error;
      }
    },
    [],
  );

  const updateExercisePreferences = useCallback(
    async (preferenceIds) => {
      await schedulerApi.updateExercisePreferences(preferenceIds);
      await queryClient.invalidateQueries({
        queryKey: ["preferences", email, currentLanguage],
      });
    },
    [queryClient, email, currentLanguage],
  );

  const getNotificationStats = useCallback(async (period = "month") => {
    return await schedulerApi.getNotificationStats(period);
  }, []);

  const markNotificationCompleted = useCallback(
    async (notificationId) => {
      await schedulerApi.markNotificationCompleted(notificationId);
      await silentRefetchSequence();
    },
    [silentRefetchSequence],
  );

  const getExerciseTypes = useCallback(async () => {
    return await schedulerApi.getExerciseTypes();
  }, []);

  const getNotificationHistory = useCallback(async (startDate, endDate) => {
    return await schedulerApi.getNotificationHistory(startDate, endDate);
  }, []);

  const snoozeNotification = useCallback(
    async (notificationId, snoozeDurationMinutes) => {
      await schedulerApi.snoozeNotification(
        notificationId,
        snoozeDurationMinutes,
      );
      await silentRefetchSequence();
    },
    [silentRefetchSequence],
  );

  const getUpcomingNotifications = useCallback(async (hours = 24) => {
    return await schedulerApi.getUpcomingNotifications(hours);
  }, []);

  const getLibraryVideos = useCallback(async (language) => {
    return await schedulerApi.getLibraryVideos(language);
  }, []);

  const getVideosForLibrary = useCallback(async (libraryId) => {
    return await schedulerApi.getVideosForLibrary(libraryId);
  }, []);

  const getVideosWatchedThisWeek = useCallback(async () => {
    return await schedulerApi.getVideosWatchedThisWeek();
  }, []);

  const recordVideoPlay = useCallback(async (videoId, playData) => {
    return await schedulerApi.recordVideoPlay(videoId, playData);
  }, []);

  const getVideoWatchHistory = useCallback(async (options = {}) => {
    return await schedulerApi.getVideoWatchHistory(options);
  }, []);

  const getCalendarToken = useCallback(async () => {
    return await schedulerApi.getCalendarToken();
  }, []);

  return {
    exerciseTypes,
    userId,
    error: homeError || preferencesError || videosError,
    calendarEvents,
    schedules,
    createNotification: createMutation.mutate,
    editNotification: editNotificationMutation.mutate,
    editSchedule: editScheduleMutation.mutate,
    deleteNotification: deleteNotificationMutation.mutate,
    deleteSchedule: deleteScheduleMutation.mutate,
    findNotificationConflict,
    refreshData: silentRefetchSequence,
    homeIsLoading: homeLoading || preferencesLoading || videosLoading,
    schedulerIsFetching: homeFetching,
    updateExercisePreferences,
    getNotificationStats,
    markNotificationCompleted,
    getExerciseTypes,
    getNotificationHistory,
    snoozeNotification,
    getUpcomingNotifications,
    getLibraryVideos,
    getVideosForLibrary,
    getVideosWatchedThisWeek,
    recordVideoPlay,
    getVideoWatchHistory,
    getCalendarToken,
  };
};

export const prefetchAllData = async (
  queryClient,
  timezoneOffset,
  currentLanguage = "de",
  email = "",
  currentProduct = null,
  onboarding_completed = true,
) => {
  const results = {
    en: { success: false, errors: [] },
    de: { success: false, errors: [] },
  };

  await prefetchLanguage(
    queryClient,
    currentLanguage,
    timezoneOffset,
    email,
    currentProduct,
    results,
    onboarding_completed,
  );

  const otherLanguage = currentLanguage === "en" ? "de" : "en";

  setTimeout(async () => {
    await prefetchLanguage(
      queryClient,
      otherLanguage,
      timezoneOffset,
      email,
      currentProduct,
      results,
      onboarding_completed,
    );
  }, 5000);

  return results;
};

const prefetchLanguage = async (
  queryClient,
  language,
  timezoneOffset,
  email,
  currentProduct,
  results,
  onboarding_completed = true,
) => {
  try {
    const fetchPromises = [];

    // ALWAYS FETCH: Videos (needed regardless of onboarding status)
    fetchPromises.push(
      queryClient
        .fetchQuery({
          queryKey: ["videos", email, language],
          queryFn: async () => {
            const response = await schedulerApi.getLibraryVideos(language);
            return {
              allVideos: response.allVideos || [],
              physioVideos: response.physioVideos || [],
            };
          },
          staleTime: 10 * 60 * 1000,
          onSuccess: (data) => {
            const all = [
              ...(data.allVideos || []),
              ...(data.physioVideos || []),
            ];
            if (all.length > 0) {
              preloadThumbnails(all);
            }
          },
        })
        .catch((error) => {
          console.error("[useScheduler] Videos fetch failed", { error });
          results[language].errors.push("videos");
        }),
    );

    // CONDITIONAL FETCH: Settings (only after onboarding is complete)
    // Skip during onboarding as user hasn't configured settings yet
    // physio_user bypasses onboarding check
    if (onboarding_completed || currentProduct === "physio_user") {
      fetchPromises.push(
        queryClient
          .fetchQuery({
            queryKey: ["settings", email],
            queryFn: async () => {
              const response = await settingsApiService.getCompleteSettings();
              return {
                standConfig: response.standConfig || {
                  startTime: 9.0,
                  endTime: 17.0,
                  frequency: 1.0,
                  daysOfWeek: [],
                  isEnabled: false,
                  offset: null,
                },
                excerciseConfig: response.excerciseConfig || {
                  startTime: 9.0,
                  endTime: 17.0,
                  frequency: 1.0,
                  daysOfWeek: [],
                  isEnabled: false,
                  isPhysioEnabled: false,
                  preferences: [],
                },
              };
            },
            staleTime: 5 * 60 * 1000,
          })
          .catch((error) => {
            console.error("[useScheduler] Settings fetch failed", { error });
            results[language].errors.push("settings");
          }),
      );
    } else {
    }

    // ALWAYS FETCH: Therapist History (for physio therapists only)
    if (currentProduct === "physio_therapist") {
      fetchPromises.push(
        queryClient
          .fetchQuery({
            queryKey: ["therapistHistory", email, language],
            queryFn: async () => {
              const response = await axios.get(
                `${process.env.REACT_APP_FIREBASE_API_HOST}/api/physio/clients/${language}`,
                {
                  withCredentials: true,
                },
              );
              return response.data.clients || [];
            },
            staleTime: 5 * 60 * 1000,
          })
          .catch((error) => {
            console.error("[useScheduler] Therapist history fetch failed", {
              error,
            });
            results[language].errors.push("therapistHistory");
          }),
      );
    }

    // CONDITIONAL FETCH: HomeData and Preferences (for NON-physio users)
    // HomeData is skipped during onboarding as there are no notifications/schedules yet
    // Preferences are always fetched (needed during onboarding Step One)
    if (currentProduct !== "physio_therapist") {
      // CONDITIONAL: HomeData (only after onboarding)
      // physio_user bypasses onboarding check
      if (onboarding_completed || currentProduct === "physio_user") {
        fetchPromises.push(
          queryClient
            .fetchQuery({
              queryKey: ["homeData", email, timezoneOffset, language],
              queryFn: async () => {
                const response = await schedulerApi.getHomeData(
                  timezoneOffset,
                  language,
                );
                // Get videosData from cache (already fetched above)
                const videosData = queryClient.getQueryData([
                  "videos",
                  email,
                  language,
                ]) || { allVideos: [], physioVideos: [] };

                // UPDATED: Pass videosData to deriveCalendarEventsInScheduler
                const calendarEvents = deriveCalendarEventsInScheduler(
                  response.notifications || [],
                  videosData || { allVideos: [], physioVideos: [] },
                );

                const allSchedules =
                  response.notifications?.flatMap(
                    (notification) => notification.schedules || [],
                  ) || [];
                return {
                  notifications: response.notifications || [],
                  schedules: allSchedules,
                  calendarEvents,
                  rawHomeData: response,
                };
              },
              staleTime: 5 * 60 * 1000,
            })
            .catch((error) => {
              console.error("[useScheduler] Home data fetch failed", { error });
              results[language].errors.push("calendar");
            }),
        );
      } else {
      }

      // ALWAYS: Preferences (needed during onboarding for exercise type selection)
      fetchPromises.push(
        queryClient
          .fetchQuery({
            queryKey: ["preferences", email, language],
            queryFn: async () => {
              const response =
                await schedulerApi.getExercisePreferences(language);
              const rawPreferences = response.preferences || [];
              const randomPreference = rawPreferences.find(
                (pref) => pref.name === "Random",
              );
              return {
                rawPreferences,
                randomPreferenceId: randomPreference?.id || null,
              };
            },
            staleTime: 10 * 60 * 1000,
          })
          .catch((error) => {
            console.error("[useScheduler] Preferences fetch failed", { error });
            results[language].errors.push("preferences");
          }),
      );
    }

    await Promise.allSettled(fetchPromises);

    results[language].success = results[language].errors.length < 3;
  } catch (error) {
    console.error("[useScheduler] Critical error for language", {
      language,
      error,
    });
    results[language].success = false;
    results[language].errors.push("critical");
  }
};

// Helper function for mutations to derive calendar events during optimistic updates
export function deriveCalendarEventsInScheduler(notifications, videosData) {
  if (!notifications || !Array.isArray(notifications)) {
    return [];
  }

  const calendarEvents = [];
  const combinedVideos = [
    ...(videosData?.allVideos || []),
    ...(videosData?.physioVideos || []),
  ];

  notifications.forEach((notification) => {
    const schedules = notification.schedules || [];

    schedules.forEach((schedule) => {
      if (schedule.type === "PHYSIO" && !schedule.video?.title) {
        return;
      }

      let color;
      switch (schedule.type) {
        case "SYSTEM":
          color = "black";
          break;
        case "USER":
          color = "#073bd0";
          break;
        case "PHYSIO":
          color = "#05b67f";
          break;
        default:
          color = "#063bd0";
      }

      let title;
      if (schedule.video?.title) {
        title = schedule.video.title;
      } else if (schedule.preferenceId === 2 && combinedVideos.length > 0) {
        const isTempId =
          typeof schedule.id === "string" && schedule.id.startsWith("temp-");

        if (isTempId) {
          title = schedule.name;
        } else {
          const matchingVideo = getRandomVideoForPhysio(
            combinedVideos,
            schedule.id,
          );
          title = matchingVideo?.title || schedule.name;
        }
      } else {
        title = schedule.name;
      }

      calendarEvents.push({
        title: title,
        date: schedule.notificationTime,
        color: color,
        meridiem: "short",
        id: schedule.id,
      });
    });
  });

  return calendarEvents;
}
